import { useEffect, useState, type FormEvent } from 'react'
import { useLocation } from 'react-router-dom'
import { ImageOff, Loader2, PackageCheck, Plus, RefreshCw, Trash2, Warehouse } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { apiClient } from '@/lib/api/client'
import type {
  ConsignmentDomesticPackage,
  ConsignmentGathering,
  DomesticShipmentWarehouseParcel,
} from '@/lib/api/types'
import { cn } from '@/lib/utils'
import { ParcelTrackingPanel } from '@/src/components/tracking/ParcelTrackingPanel'
import {
  CHINA_TRACKING_STATUS,
  GATHERING_STATE,
  formatKg,
  formatShipWeek,
  gatheringSummary,
} from '@/src/lib/parcels'

/**
 * Gathering a consignment's items at our China warehouse: suppliers send the
 * items to Guangzhou so they can leave China together. One card per package
 * (a domestic tracking number), with the items in it, its courier - status,
 * latest event, a map and every update - and, once the warehouse team has
 * received a parcel under that number, when it arrived, its weight and photos.
 * Progress is counted in items. This leg ends at the warehouse; the
 * consignment's air or sea leg is separate. Admins add a package by hand and
 * ask the courier again; everyone else reads.
 */
export function GatheringInChinaCard({
  consignmentId,
  gathering,
  isAdmin,
  onChanged,
}: {
  consignmentId: string
  gathering: ConsignmentGathering
  isAdmin: boolean
  onChanged: (gathering: ConsignmentGathering) => void
}) {
  const { summary, packages, items_without_number: unpacked } = gathering
  const location = useLocation()

  // The Items tab links to a package with #package-<id>.
  useEffect(() => {
    if (!location.hash.startsWith('#package-')) return
    document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: 'start' })
  }, [location.hash])

  return (
    <Card role="region" aria-labelledby={`gathering-${consignmentId}`}>
      <CardHeader>
        <CardTitle id={`gathering-${consignmentId}`} className="flex items-center gap-2">
          <Warehouse className="h-5 w-5" aria-hidden />
          Gathering in China
        </CardTitle>
        <CardDescription>
          Suppliers send the items to our warehouse in Guangzhou, so they leave China together.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2" role="status" aria-label="Gathering progress">
          <p className={cn('font-semibold', summary.all_at_warehouse && 'text-done')}>
            {gatheringSummary(summary)}
          </p>
          {summary.items_total > 0 && summary.items_with_number > 0 && (
            <Progress
              value={(summary.items_at_warehouse / summary.items_total) * 100}
              aria-label="Items at our China warehouse"
            />
          )}
        </div>

        {packages.length > 0 && (
          <ul className="space-y-4" aria-label="Packages to our China warehouse">
            {packages.map((pkg) => (
              <li
                key={pkg.id}
                id={`package-${pkg.id}`}
                aria-labelledby={`package-title-${pkg.id}`}
                className="scroll-mt-20 rounded-lg border p-3 sm:p-4"
              >
                <PackageCard
                  consignmentId={consignmentId}
                  pkg={pkg}
                  isAdmin={isAdmin}
                  onChanged={onChanged}
                />
              </li>
            ))}
          </ul>
        )}

        {unpacked.length > 0 && (
          <section className="space-y-2 rounded-lg border border-dashed p-3 text-sm">
            <h3 className="font-medium">No domestic tracking number yet</h3>
            <ul className="space-y-1" aria-label="Items with no domestic tracking number">
              {unpacked.map((item) => (
                <li key={item.id}>
                  {item.quantity} × {item.name}
                </li>
              ))}
            </ul>
            {isAdmin && (
              <p className="text-xs text-muted-foreground">
                Add the package&apos;s tracking number on each item in the Items tab.
              </p>
            )}
          </section>
        )}

        {packages.length === 0 && unpacked.length === 0 && (
          <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            No items or domestic tracking numbers yet.
          </p>
        )}

        {isAdmin && <AddDomesticNumber consignmentId={consignmentId} onChanged={onChanged} />}
      </CardContent>
    </Card>
  )
}

function PackageCard({
  consignmentId,
  pkg,
  isAdmin,
  onChanged,
}: {
  consignmentId: string
  pkg: ConsignmentDomesticPackage
  isAdmin: boolean
  onChanged: (gathering: ConsignmentGathering) => void
}) {
  const [busy, setBusy] = useState<'track' | 'remove' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const state = GATHERING_STATE[pkg.state]
  const title = `package ${pkg.tracking_number}`

  const refresh = async () => {
    try {
      onChanged(await apiClient.refreshConsignmentDomesticShipment(consignmentId, pkg.id))
      return null
    } catch (cause) {
      return cause instanceof Error ? cause.message : 'Could not reach the tracking service'
    }
  }

  const run = async (what: 'track' | 'remove') => {
    setBusy(what)
    setError(null)
    if (what === 'track') {
      setError(await refresh())
    } else {
      try {
        onChanged(await apiClient.removeConsignmentDomesticShipment(consignmentId, pkg.id))
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Could not remove the package')
      }
    }
    setBusy(null)
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={`package-title-${pkg.id}`} className="font-mono text-sm font-medium break-all">
          {pkg.tracking_number}
        </h3>
        <Badge variant="outline" className={cn('py-1', state.tone)}>
          {state.label}
        </Badge>
      </div>
      {pkg.supplier_name && (
        <p className="text-sm text-muted-foreground">From {pkg.supplier_name}</p>
      )}

      {pkg.items.length > 0 ? (
        <div className="text-sm">
          <p className="text-muted-foreground">In this package</p>
          <ul aria-label={`Items in ${title}`}>
            {pkg.items.map((item) => (
              <li key={item.id}>
                {item.quantity} × {item.name}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No item names this number yet.</p>
      )}

      {pkg.warehouse_parcels.map((parcel) => (
        <WarehouseReceipt key={parcel.id} consignmentId={consignmentId} parcel={parcel} />
      ))}

      {pkg.tracking ? (
        <ParcelTrackingPanel
          tracking={pkg.tracking}
          title={title}
          initiallyOpen
          statusLabels={CHINA_TRACKING_STATUS}
          onRefresh={isAdmin ? refresh : undefined}
        />
      ) : (
        <div className="space-y-2 rounded-lg border p-3 text-sm">
          <p className="text-muted-foreground">Not followed with the courier yet.</p>
          {isAdmin && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 sm:h-8"
              onClick={() => run('track')}
              disabled={busy !== null}
            >
              {busy === 'track' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Track with the courier
            </Button>
          )}
        </div>
      )}

      {isAdmin && pkg.source === 'MANUAL' && pkg.items.length === 0 && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2 h-10 text-muted-foreground hover:text-destructive sm:h-8"
          onClick={() => run('remove')}
          disabled={busy !== null}
        >
          {busy === 'remove' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          Remove this package
        </Button>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}

/** What the warehouse team recorded for a parcel received under the package's number. */
function WarehouseReceipt({
  consignmentId,
  parcel,
}: {
  consignmentId: string
  parcel: DomesticShipmentWarehouseParcel
}) {
  const received = parcel.received_at && parcel.status !== 'EXPECTED'
  return (
    <div className="space-y-2 rounded-lg bg-muted/60 p-3 text-sm">
      <p className="flex flex-wrap items-center gap-2 font-medium">
        {received ? (
          <PackageCheck className="h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <Warehouse className="h-4 w-4 shrink-0" aria-hidden />
        )}
        {received
          ? `Received at the warehouse on ${formatShipWeek(parcel.received_at!.slice(0, 10))}`
          : 'Expected at the warehouse'}
        {parcel.status === 'CONSOLIDATED' && <Badge variant="outline">On a consignment</Badge>}
      </p>
      {(parcel.weight_kg != null || parcel.chargeable_kg != null) && (
        <p className="text-muted-foreground">
          {[
            parcel.weight_kg != null ? `Weighs ${formatKg(parcel.weight_kg)}` : null,
            parcel.chargeable_kg != null && parcel.chargeable_kg !== parcel.weight_kg
              ? `charged as ${formatKg(parcel.chargeable_kg)}`
              : null,
          ]
            .filter(Boolean)
            .join(', ')}
        </p>
      )}
      {parcel.photo_ids.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Warehouse photos">
          {parcel.photo_ids.map((photoId, index) => (
            <li key={photoId}>
              <ReceiptPhoto
                consignmentId={consignmentId}
                photoId={photoId}
                label={`Warehouse photo ${index + 1} of ${parcel.photo_ids.length}`}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function ReceiptPhoto({
  consignmentId,
  photoId,
  label,
}: {
  consignmentId: string
  photoId: string
  label: string
}) {
  const [photo, setPhoto] = useState<{ url: string | null; failed: boolean }>({
    url: null,
    failed: false,
  })
  useEffect(() => {
    let active = true
    let current: string | null = null
    apiClient.consignmentParcelPhotoUrl(consignmentId, photoId).then(
      (url) => {
        if (active) {
          current = url
          setPhoto({ url, failed: false })
        } else URL.revokeObjectURL(url)
      },
      () => active && setPhoto({ url: null, failed: true }),
    )
    return () => {
      active = false
      if (current) URL.revokeObjectURL(current)
    }
  }, [consignmentId, photoId])

  if (photo.failed) {
    return (
      <div
        role="img"
        aria-label={`${label} could not be loaded`}
        className="flex size-20 items-center justify-center rounded-md border bg-muted text-muted-foreground"
      >
        <ImageOff className="h-5 w-5" aria-hidden />
      </div>
    )
  }
  if (!photo.url) {
    return (
      <div
        className="size-20 animate-pulse rounded-md border bg-muted"
        aria-label="Loading photo"
      />
    )
  }
  return (
    <a href={photo.url} target="_blank" rel="noreferrer" aria-label={`Open ${label}`}>
      <img src={photo.url} alt={label} className="size-20 rounded-md border object-cover" />
    </a>
  )
}

function AddDomesticNumber({
  consignmentId,
  onChanged,
}: {
  consignmentId: string
  onChanged: (gathering: ConsignmentGathering) => void
}) {
  const [number, setNumber] = useState('')
  const [supplier, setSupplier] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const add = async (event: FormEvent) => {
    event.preventDefault()
    const trackingNumber = number.trim()
    if (trackingNumber.length < 4) {
      setError('Enter the tracking number the supplier gave you.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      onChanged(
        await apiClient.addConsignmentDomesticShipment(consignmentId, {
          tracking_number: trackingNumber,
          supplier_name: supplier.trim() || null,
        }),
      )
      setNumber('')
      setSupplier('')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not add the tracking number')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      onSubmit={add}
      noValidate
      aria-label="Add a package by hand"
      className="grid gap-3 rounded-md border p-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
    >
      <div className="space-y-1.5">
        <Label htmlFor={`domestic-number-${consignmentId}`}>Package tracking number</Label>
        <Input
          id={`domestic-number-${consignmentId}`}
          value={number}
          onChange={(event) => {
            setNumber(event.target.value)
            setError(null)
          }}
          placeholder="e.g. JT0012345678901"
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          maxLength={60}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`domestic-supplier-${consignmentId}`}>Supplier (optional)</Label>
        <Input
          id={`domestic-supplier-${consignmentId}`}
          value={supplier}
          onChange={(event) => setSupplier(event.target.value)}
          maxLength={200}
        />
      </div>
      <Button type="submit" className="h-10" disabled={busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        Add and track
      </Button>
      {error && (
        <p role="alert" className="text-sm text-destructive sm:col-span-3">
          {error}
        </p>
      )}
    </form>
  )
}
